import { MathUtils } from '@skand/three-js-renderer/bin/private/math';
import { Line3, Matrix3, Matrix4, Plane, Quaternion, Vector3, Vector4 } from 'three';
/**
 * Defines the behavior of a circle path
 */
export class CirclePathObject {
    constructor(renderer, origin, center, normal, pivotCenter) {
        Object.defineProperty(this, "renderer", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "origin", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "center", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "normal", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "radius", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "basis", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "inverseBasis", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "pivotCenter", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "currentPosition", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "draggingPlane", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.origin = origin;
        this.center = center;
        this.normal = normal;
        this.radius = this.center.distanceTo(this.origin);
        this.basis = this.calculateBasis();
        this.inverseBasis = this.basis.clone().invert();
        this.pivotCenter = pivotCenter;
        if (this.pivotCenter) {
            this.currentPosition = this.center.clone();
        }
        else {
            this.currentPosition = this.origin.clone();
        }
        this.draggingPlane = new Plane();
        this.renderer = renderer;
    }
    /**
     * Calculate the basis vector matrix to transform between
     * world space and the local space of this circle
     */
    calculateBasis() {
        const zAxis = this.normal.clone();
        const xAxis = this.origin.clone().sub(this.center).normalize();
        const yAxis = this.normal.clone().cross(xAxis).normalize();
        return new Matrix3().set(xAxis.x, yAxis.x, zAxis.x, xAxis.y, yAxis.y, zAxis.y, xAxis.z, yAxis.z, zAxis.z);
    }
    /**
     * Calculate the angle formed by the given position on the circle
     * from the origin
     */
    calculateAngle(position) {
        // Convert to the current widget position and center
        // to standard basis to extract the x-y components
        const projectedCenter = this.center.clone().applyMatrix3(this.inverseBasis);
        const projectedPosition = position.clone().applyMatrix3(this.inverseBasis);
        // Use the projected coordinates to calculate theta
        let theta = Math.atan2(projectedPosition.y - projectedCenter.y, projectedPosition.x - projectedCenter.x);
        // Clip the value between [0, 2 * Math.PI)
        if (theta < 0)
            theta += 2 * Math.PI;
        return theta;
    }
    /**
     * Calculate the dragging plane on which the widget moves relative
     * to the camera
     */
    onGrab() {
        this.draggingPlane.setFromNormalAndCoplanarPoint(this.normal, this.center);
    }
    /**
     * Do nothing on release
     */
    onRelease() { }
    /**
     * Move the object along the circumference of the circle by calculating the
     * widget position's change in angle from the origin using the
     * projected 2D motion
     */
    mapMotion(position, gesture) {
        const camera = this.renderer.getWorld().getCamera();
        const cameraPosition = new Vector3();
        camera.getPositionECEF(cameraPosition);
        const cameraRotation = new Quaternion();
        camera.getRotation(cameraRotation);
        // Get NDC on near plane for last cursor position
        const size = this.renderer.getSize();
        const prevRayDir = MathUtils.screenCoordsToNDC(gesture.prevPosition.x, gesture.prevPosition.y, size.x, size.y, new Vector4(0, 0, 0, 1));
        prevRayDir.y *= -1;
        // Get NDC on far plane for last cursor position
        const currRayDir = MathUtils.screenCoordsToNDC(gesture.position.x, gesture.position.y, size.x, size.y, new Vector4(0, 0, 0, 1));
        currRayDir.y *= -1;
        // Get direction to near plane by applying inverse projection and dividing by W
        const invProj = new Matrix4();
        camera.getPerspective().getInverseProjectionMatrix(invProj);
        prevRayDir.applyMatrix4(invProj);
        prevRayDir.divideScalar(prevRayDir.w);
        currRayDir.applyMatrix4(invProj);
        currRayDir.divideScalar(currRayDir.w);
        const length = position.length() * 2;
        const ray = new Line3(cameraPosition);
        // Find the intersection of these rays with the dragging plane
        ray.end.x = prevRayDir.x;
        ray.end.y = prevRayDir.y;
        ray.end.z = prevRayDir.z;
        ray.end.normalize().multiplyScalar(length).applyQuaternion(cameraRotation).add(cameraPosition);
        const prevIntersect = new Vector3();
        this.draggingPlane.intersectLine(ray, prevIntersect);
        ray.end.x = currRayDir.x;
        ray.end.y = currRayDir.y;
        ray.end.z = currRayDir.z;
        ray.end.normalize().multiplyScalar(length).applyQuaternion(cameraRotation).add(cameraPosition);
        const currIntersect = new Vector3();
        this.draggingPlane.intersectLine(ray, currIntersect);
        // Calculate angles for all relevant positions
        const startAngle = this.calculateAngle(prevIntersect);
        const endAngle = this.calculateAngle(currIntersect);
        const currentAngle = this.calculateAngle(this.currentPosition);
        // Set the current position based on the translated angle of the widget
        const theta = currentAngle + (endAngle - startAngle);
        const localPosition = new Vector3(Math.cos(theta) * this.radius, Math.sin(theta) * this.radius, 0);
        this.currentPosition.copy(localPosition.applyMatrix3(this.basis).add(this.center));
    }
    /**
     * Get the target position for rendering
     */
    getWidgetPosition() {
        if (this.pivotCenter) {
            return this.center;
        }
        else {
            return this.currentPosition;
        }
    }
    /**
     * Calculate the clockwise angular difference between the current
     * position and the origin (mapped to [0, 1] range)
     */
    getProgress() {
        const angle = this.calculateAngle(this.currentPosition);
        return angle / (2 * Math.PI);
    }
    /**
     * Set the current position based on the progress value [0, 1]
     */
    setFromProgress(progress) {
        const angle = Math.PI * 2 * progress;
        this.currentPosition = this.origin.clone().applyAxisAngle(this.normal, angle);
    }
}
